// Render-engine capability detection (Local Engine Phase 1).
// Pure, side-effect-free detection of the browser/device features the future
// client-side (WebCodecs/WebGPU/OPFS) renderer will rely on. Detecting an API's
// PRESENCE is not the same as it WORKING or being hardware-accelerated — the
// WebCodecs PoC (webcodecsPoc.js) measures that separately.

function has(obj, key) {
  try { return typeof obj !== "undefined" && key in obj; } catch { return false; }
}

function wasmSimd() {
  // Minimal WASM module using a v128 (SIMD) instruction. Validates only if SIMD is supported.
  try {
    const bytes = new Uint8Array([
      0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0,
      10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
    ]);
    return WebAssembly.validate(bytes);
  } catch { return false; }
}

export async function detectCapabilities() {
  const caps = {
    videoDecoder: typeof window !== "undefined" && "VideoDecoder" in window,
    videoEncoder: typeof window !== "undefined" && "VideoEncoder" in window,
    webCodecs: typeof window !== "undefined" && "VideoEncoder" in window && "VideoDecoder" in window,
    offscreenCanvas: typeof window !== "undefined" && "OffscreenCanvas" in window,
    webGL2: false,
    webGPU: typeof navigator !== "undefined" && "gpu" in navigator,
    sharedArrayBuffer: typeof SharedArrayBuffer !== "undefined",
    crossOriginIsolated: typeof window !== "undefined" && !!window.crossOriginIsolated,
    opfs: typeof navigator !== "undefined" && has(navigator, "storage") && !!navigator.storage?.getDirectory,
    fileSystemAccess: typeof window !== "undefined" && "showOpenFilePicker" in window,
    wasmSimd: wasmSimd(),
    wasmThreads: typeof SharedArrayBuffer !== "undefined",
    hardwareEncoding: false,
    hardwareDecoding: false,
    codecs: {},
    deviceMemoryGB: (typeof navigator !== "undefined" && navigator.deviceMemory) || null,
    hardwareConcurrency: (typeof navigator !== "undefined" && navigator.hardwareConcurrency) || null,
  };

  // WebGL2
  try {
    const c = document.createElement("canvas");
    caps.webGL2 = !!c.getContext("webgl2");
  } catch { caps.webGL2 = false; }

  // WebGPU adapter (presence of the object doesn't guarantee an adapter)
  if (caps.webGPU) {
    try {
      const adapter = await navigator.gpu.requestAdapter();
      caps.webGPU = !!adapter;
    } catch { caps.webGPU = false; }
  }

  // Codec + hardware-acceleration probing via VideoEncoder.isConfigSupported.
  if (caps.videoEncoder && window.VideoEncoder?.isConfigSupported) {
    const configs = {
      h264: { codec: "avc1.42001f" },
      vp9: { codec: "vp09.00.10.08" },
      av1: { codec: "av01.0.04M.08" },
    };
    for (const [name, base] of Object.entries(configs)) {
      const cfg = { ...base, width: 1280, height: 720, bitrate: 4_000_000, framerate: 30 };
      try {
        const sw = await window.VideoEncoder.isConfigSupported({ ...cfg, hardwareAcceleration: "prefer-software" });
        const hw = await window.VideoEncoder.isConfigSupported({ ...cfg, hardwareAcceleration: "prefer-hardware" });
        caps.codecs[name] = { supported: !!(sw?.supported || hw?.supported), hardware: !!hw?.supported };
        if (hw?.supported) caps.hardwareEncoding = true;
      } catch {
        caps.codecs[name] = { supported: false, hardware: false };
      }
    }
  }

  return caps;
}

// Human-readable summary + a recommended render path for the current device.
export function summarize(caps) {
  const localCapable = caps.webCodecs && (caps.webGPU || caps.webGL2 || caps.offscreenCanvas);
  return {
    localCapable,
    // Local engine is in BETA and not yet wired to production export — server FFmpeg
    // remains the actual export path. This only reports readiness.
    recommendedPath: "server",
    reason: localCapable
      ? "Device supports WebCodecs client-side rendering (local engine is in beta; server export is still used)."
      : "WebCodecs/GPU path unavailable — server FFmpeg renderer is used.",
  };
}
